import { FC, useEffect, useState } from "react";
import { Modal, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format, parseISO } from "date-fns";
import { X, ShieldCheck } from "lucide-react";

import { DatePicker } from "../../atoms/custom-date-time-picker/DateRangePicker";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import {
  addTaxExemption,
  deleteTaxExemption,
  getTaxExemptionByCustomer,
  updateTaxExemption,
} from "../../../redux/actions/taxExemptionAction";
import { TaxExemptionSliceActions } from "../../../redux/slice/taxExemptionSlice";
import { Colors } from "../../../configs/colors";

export interface TaxExceptionModalProps {
  open: boolean;
  onClose(): void;
  customerId: number;
}

const schema = yup.object().shape({
  taxID: yup.string().required("Tax ID is required"),
  expiryDate: yup
    .date()
    .nullable()
    .typeError("Expiry Date is required")
    .required("Expiry Date is required"),
});

const TaxExceptionModal: FC<TaxExceptionModalProps> = ({
  open,
  onClose,
  customerId,
}) => {
  const dispatch = useAppDispatch();
  const { currentExemption, fetchLoading, mutationLoading } = useAppSelector(
    (state) => state.taxExemption
  );

  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  useEffect(() => {
    if (open && customerId) {
      dispatch(getTaxExemptionByCustomer(customerId));
    }
    if (!open) {
      reset();
      setExpiryDate(null);
      dispatch(TaxExemptionSliceActions.clearTaxExemption());
    }
  }, [open, customerId, dispatch, reset]);

  useEffect(() => {
    if (currentExemption) {
      setValue("taxID", currentExemption.taxId);
      const parsed = parseISO(currentExemption.expiryDate);
      setExpiryDate(parsed);
      setValue("expiryDate", parsed);
    }
  }, [currentExemption, setValue]);

  const handleRemove = async () => {
    await dispatch(deleteTaxExemption(customerId));
    onClose();
  };

  const handleUpdate = handleSubmit(async (data) => {
    await dispatch(
      updateTaxExemption({
        customerId,
        taxId: data.taxID,
        expiryDate: format(data.expiryDate as Date, "yyyy-MM-dd"),
      })
    );
    onClose();
  });

  const handleAddExemption = handleSubmit(async (data) => {
    await dispatch(
      addTaxExemption({
        customerId,
        taxId: data.taxID,
        expiryDate: format(data.expiryDate as Date, "yyyy-MM-dd"),
      })
    );
    onClose();
  });

  const hasExistingExemption = !!currentExemption;

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0,0,0,0.5)" },
        },
      }}
    >
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="w-[510px] overflow-hidden rounded-[24px] border border-white/70 bg-white shadow-[0_30px_80px_rgba(0,0,0,0.26)]">
          <div className="relative bg-gradient-to-br from-white via-white to-[#D9D9D9]/20 px-10 pb-8 pt-9">
            {/* Close button */}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-8 top-8 flex h-10 w-10 items-center justify-center rounded-full border border-[#D9D9D9] bg-white text-[#515151]/75 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#D9D9D9]/30 hover:text-[#515151]"
            >
              <X size={20} strokeWidth={2.4} />
            </button>

            {/* Header */}
            <div className="mb-8 flex items-start gap-4">
              <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-brand/10 text-brand shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                <ShieldCheck size={24} strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-[2.625rem] font-bold leading-none tracking-[-0.04em] text-[#515151]">
                  Tax Exemption
                </h1>
                <p className="mt-2 text-[0.875rem] font-medium text-[#515151]/60">
                  {hasExistingExemption
                    ? "Update or remove the customer exemption details."
                    : "Add exemption details to this customer profile."}
                </p>
              </div>
            </div>

            {/* Body */}
            {fetchLoading ? (
              <div className="flex items-center justify-center py-10">
                <CircularProgress size={32} sx={{ color: Colors.PRIMARY }} />
              </div>
            ) : (
              <>
                {/* Fields card */}
                <div className="space-y-5 rounded-2xl border border-[#D9D9D9]/80 bg-white/85 p-5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.9),0_10px_24px_rgba(0,0,0,0.04)]">
                  {/* Tax ID */}
                  <div className="grid grid-cols-[120px_1fr] items-start gap-6">
                    <span className="pt-[13px] text-[1.0625rem] font-medium text-[#515151]">
                      Tax ID
                    </span>
                    <div className="flex flex-col gap-1">
                      <Controller
                        name="taxID"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <input
                            {...field}
                            type="text"
                            autoComplete="off"
                            placeholder="Tax ID here"
                            test-name="tax-id-input"
                            className={`h-[46px] w-full rounded-xl border bg-white px-4 text-[0.9375rem] text-[#515151] shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] outline-none transition-all duration-200 placeholder:text-[#515151]/45 focus:ring-4 ${
                              fieldState.invalid
                                ? "border-[#930000] focus:border-[#930000] focus:ring-[#930000]/10"
                                : "border-[#D9D9D9] focus:border-brand-secondary focus:ring-brand-secondary/10"
                            }`}
                          />
                        )}
                      />
                      {errors.taxID && (
                        <span className="text-[0.75rem] text-[#930000]">
                          {errors.taxID.message}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Exp Date */}
                  <div className="grid grid-cols-[120px_1fr] items-start gap-6">
                    <span className="pt-[13px] text-[1.0625rem] font-medium text-[#515151]">
                      Exp Date
                    </span>
                    <div className="flex flex-col gap-1">
                      <DatePicker
                        type="single"
                        value={expiryDate ?? undefined}
                        onChange={(val) => {
                          if (val instanceof Date || val === undefined) {
                            const dateToSet = val ?? null;
                            setExpiryDate(dateToSet);
                            setValue("expiryDate", dateToSet ?? new Date(), {
                              shouldValidate: true,
                            });
                            setIsCalendarOpen(false);
                          }
                        }}
                        compact
                        open={isCalendarOpen}
                        onOpenChange={setIsCalendarOpen}
                        className="h-[46px] w-full rounded-xl border border-[#D9D9D9] text-[0.9375rem]"
                        popoverProps={{
                          side: "top",
                          className:
                            "w-auto p-4 bg-white shadow-md rounded-md border z-[9999]",
                        }}
                      />
                      {errors.expiryDate && (
                        <span className="text-[0.75rem] text-[#930000]">
                          {errors.expiryDate.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div
                  className={
                    hasExistingExemption
                      ? "mt-8 grid grid-cols-2 gap-4"
                      : "mt-8"
                  }
                >
                  {hasExistingExemption && (
                    <button
                      type="button"
                      onClick={handleRemove}
                      disabled={mutationLoading}
                      test-name="remove-exemption-button"
                      className="flex h-[56px] items-center justify-center rounded-xl bg-[#930000] px-7 text-[1.0625rem] font-semibold tracking-[-0.01em] text-white shadow-[0_10px_22px_rgba(147,0,0,0.18)] transition-all duration-200 hover:bg-[#7f0000] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#515151]/45 disabled:shadow-none"
                    >
                      {mutationLoading ? (
                        <CircularProgress size={22} sx={{ color: "white" }} />
                      ) : (
                        "Remove"
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={hasExistingExemption ? handleUpdate : handleAddExemption}
                    disabled={!isValid || mutationLoading}
                    test-name={
                      hasExistingExemption
                        ? "update-exemption-button"
                        : "add-exception-button"
                    }
                    className="flex h-[56px] items-center justify-center rounded-xl bg-brand px-7 text-[1.0625rem] font-semibold tracking-[-0.01em] text-white shadow-[0_10px_22px_rgb(var(--brand-primary-rgb)/0.22)] transition-all duration-200 hover:bg-[#017846] active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-[#D9D9D9] disabled:text-[#515151]/45 disabled:shadow-none"
                  >
                    {mutationLoading ? (
                      <CircularProgress size={22} sx={{ color: "white" }} />
                    ) : hasExistingExemption ? (
                      "Update"
                    ) : (
                      "Add Exemption"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TaxExceptionModal;
